if (!customElements.get('sticky-feature')) {
	class StickyFeature extends HTMLElement {
		constructor() {
			super()
			this.sectionId = this.dataset.sectionId
			this.contentSections = []
			this.featureImages = []
			this.paginationBullets = []
			this.observer = null
			this.sectionObserver = null
			this.isInitialized = false
			this.reinitTimeout = null
		}

		connectedCallback() {
			// Initialise on document ready
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => this.init())

				// Initialise on shopify design mode load
				if (Shopify?.designMode) {
					this.designModeLoadListener = event => {
						if (event.target.id === `shopify-section-${this.sectionId}`) {
							this.init()
						}
					}
					document.addEventListener('shopify:section:load', this.designModeLoadListener)
				}
			} else {
				this.init()
			}
		}

		init() {
			if (this.isInitialized) return

			this.cacheElements()
			if (!this.validateElements()) {
				return
			}

			this.setupIntersectionObserver()
			this.isInitialized = true

			if (Shopify?.designMode) {
				this.setupSectionObserver()
			}
		}

		cacheElements() {
			this.contentSections = Array.from(this.querySelectorAll('.js-content-section'))
			this.featureImages = Array.from(this.querySelectorAll('.js-sticky-image'))
			this.paginationBullets = Array.from(this.querySelectorAll('.js-pagination-bullet'))
		}

		validateElements() {
			return (
				this.contentSections.length > 0 &&
				this.featureImages.length <= this.contentSections.length &&
				this.paginationBullets.length === this.contentSections.length
			)
		}

		setupIntersectionObserver() {
			const options = {
				threshold: 0.5,
				rootMargin: '0px',
			}

			this.observer = new IntersectionObserver(
				(entries) => {
					let mostVisibleSection = null
					let maxRatio = 0

					entries.forEach((entry) => {
						if (entry.intersectionRatio > maxRatio) {
							maxRatio = entry.intersectionRatio
							mostVisibleSection = entry.target
						}
					})

					if (mostVisibleSection && maxRatio >= 0.5) {
						this.handleSectionIntersection(mostVisibleSection)
					}
				},
				options
			)

			this.contentSections.forEach((section) => {
				this.observer.observe(section)
			})

			const firstSection = this.contentSections[0]
			if (firstSection) {
				this.setActiveImage(firstSection.dataset.index)
				this.setActivePagination(firstSection.dataset.index)
				this.updateColourScheme(firstSection.dataset.colourScheme)
			}
		}

		handleSectionIntersection(section) {
			const sectionIndex = parseInt(section.dataset.index, 10)
			this.setActiveImage(sectionIndex)
			this.setActivePagination(sectionIndex)
			this.updateColourScheme(section.dataset.colourScheme)
		}

		setActiveImage(index) {
			this.featureImages.forEach(image => image.classList.remove('active'))
			if (this.featureImages[index]) {
				this.featureImages[index].classList.add('active')
			}
		}

		setActivePagination(index) {
			this.paginationBullets.forEach(bullet => bullet.classList.remove('active'))
			if (this.paginationBullets[index]) {
				this.paginationBullets[index].classList.add('active')
			}
		}

		updateColourScheme(colourScheme) {
			this.classList.remove('t-light', 't-dark')

			if (colourScheme) {
				this.classList.add(`t-${colourScheme}`)
			}
		}

		setupSectionObserver() {
			this.sectionObserver = new MutationObserver(() => {
				this.reinitializeDebounced()
			})

			this.sectionObserver.observe(this, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ['data-index', 'class'],
			})
		}

		reinitializeDebounced() {
			if (this.reinitTimeout) {
				clearTimeout(this.reinitTimeout)
			}
			this.reinitTimeout = setTimeout(() => {
				this.reinitialize()
			}, 200)
		}

		reinitialize() {
			if (this.observer) {
				this.observer.disconnect()
			}

			this.cacheElements()
			if (this.validateElements()) {
				this.setupIntersectionObserver()
			}
		}

		disconnectedCallback() {
			if (this.observer) {
				this.observer.disconnect()
			}
			if (this.sectionObserver) {
				this.sectionObserver.disconnect()
			}
			if (this.designModeLoadListener && Shopify?.designMode) {
				document.removeEventListener('shopify:section:load', this.designModeLoadListener)
			}
			if (this.reinitTimeout) {
				clearTimeout(this.reinitTimeout)
			}
			this.isInitialized = false
		}
	}

	customElements.define('sticky-feature', StickyFeature)
}
